/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Grid, PageGridContainer, breakpoints } from '@coursera/cds-core';
import { useVisibilityTracker } from '@coursera/event-pulse/react';

import TrackedDiv from 'bundles/page/components/TrackedDiv';
import Section from 'bundles/unified-description-page-common/components/shared/Section';
import { CREDITS_ANCHOR_ID } from 'bundles/unified-description-page-common/constants';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';

type Props = {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
  sectionSpacing?: number;
};

const styles = {
  moduleContent: () => css`
    background: var(--cds-color-grey-25);
    padding: var(--cds-spacing-400);
    border-radius: 8px;
    ${breakpoints.down('xs')} {
      padding: var(--cds-spacing-200);
    }
  `,
  title: () => css`
    margin-bottom: var(--cds-spacing-200);
  `,
  ctaContainer: () => css`
    margin-top: var(--cds-spacing-150);
  `,
  subtitleContainer: () => css`
    margin-top: var(--cds-spacing-400);
    padding-top: var(--cds-spacing-300);
    border-top: 2px solid var(--cds-color-grey-50);
  `,
};

export const EligibleDegreesModule = ({ title, subtitle, children, sectionSpacing }: Props) => {
  const eventingProductData = useEventingProductData();

  const visibilityTrackingRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_page_section',
    {
      pageSection: {
        sectionName: 'eligible_degree',
      },
      ...eventingProductData,
    }
  );

  return (
    <TrackedDiv
      trackingName="eligible_degrees_module"
      trackClicks={false}
      requireFullyVisible={false}
      withVisibilityTracking={true}
      data-e2e="eligible_degrees_module"
    >
      <Section anchorId={CREDITS_ANCHOR_ID} ref={visibilityTrackingRef} spacing={sectionSpacing}>
        <PageGridContainer>
          <Grid item xs={12}>
            <div css={styles.moduleContent}>
              {title && <div css={styles.title}>{title}</div>}
              {subtitle && <div css={styles.subtitleContainer}>{subtitle}</div>}
              <div>{children}</div>
            </div>
          </Grid>
        </PageGridContainer>
      </Section>
    </TrackedDiv>
  );
};
